import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { Panel, PanelHead } from '../../../music24/components/primitives/Panel';
import { MonthlyAddOnsEditor } from './MonthlyAddOnsEditor';
import { MonthlyAddOnsNotice, noticeLead } from './MonthlyAddOnsNotice';
import { useMonthlyAddOns } from './useMonthlyAddOns';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the add-ons panel; each part uses its own slice of it */
import styles from './MonthlyAddOns.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings -> Subscription, "Monthly add-ons" (spec 2026-10-19, design monthly-addons a, b): extra
 * scheduled posts and extra video credits on the web plan, and the extra artists of Plus, Pro, Max
 * and Ultra (each at the plan price minus 20 %; the only place that sells them). The owner of a web plan gets the
 * sliders; a member, a store subscriber, Free, the trial and a plan with no web subscription get
 * their note. Every state reads `billing.monthlyAddOns`.
 */
const MonthlyAddOnsSectionComponent: React.FC = () => {
  const intl = useIntl();
  const { data, isLoading, error, refetch, save } = useMonthlyAddOns();
  const title = intl.formatMessage(messages.title);

  if (isLoading) {
    return (
      <Panel as="section" data-testid="addons-loading">
        <PanelHead titleAs="h2" title={title} />
        <div className={styles.body}>
          <div className={styles.skeleton} style={{ width: '45%' }} />
          <div className={styles.skeleton} style={{ width: '100%', height: 6 }} />
          <div className={styles.skeleton} style={{ width: '35%' }} />
        </div>
      </Panel>
    );
  }
  if (error !== null || data === undefined) {
    return (
      <Panel as="section">
        <PanelHead titleAs="h2" title={title} />
        <ErrorView error={error ?? undefined} retry={refetch} />
      </Panel>
    );
  }

  const isEditor = data.availability === 'available';
  return (
    <Panel as="section" data-testid="addons-section">
      <PanelHead
        titleAs="h2"
        title={title}
        subtitle={intl.formatMessage(noticeLead(data))}
        right={
          isEditor ? (
            <span className={styles.meta}>
              {intl.formatMessage(data.interval === 'yearly' ? messages.billedYearly : messages.billedMonthly)}
            </span>
          ) : undefined
        }
      />
      {isEditor ? <MonthlyAddOnsEditor data={data} save={save} /> : <MonthlyAddOnsNotice data={data} />}
    </Panel>
  );
};

export const MonthlyAddOnsSection = TypedMemo(MonthlyAddOnsSectionComponent);
